/**
 * Vendor Login & Registration Page
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Building2, LogIn, UserPlus, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VendorLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(inviteToken ? 'register' : 'login');

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({
    company_name: '', contact_name: '', email: '', phone: '',
    password: '', business_type: '', license_number: '', address: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/vendor/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });
      const data = await res.json();
      if (res.ok && data.access_token) {
        localStorage.setItem('vendor_token', data.access_token);
        localStorage.setItem('vendor_data', JSON.stringify(data.vendor));
        toast.success(`Welcome back, ${data.vendor.company_name || data.vendor.contact_name}!`);
        navigate('/vendor/dashboard');
      } else {
        toast.error(data.detail || 'Login failed');
      }
    } catch (e) {
      toast.error('Connection error');
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/vendor/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registerForm)
      });
      const data = await res.json();
      if (res.ok && data.access_token) {
        localStorage.setItem('vendor_token', data.access_token);
        localStorage.setItem('vendor_data', JSON.stringify(data.vendor));
        toast.success('Registration successful! Welcome to the Vendor Portal.');
        navigate('/vendor/dashboard');
      } else {
        toast.error(data.detail || 'Registration failed');
      }
    } catch (e) {
      toast.error('Connection error');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4 py-8" data-testid="vendor-login-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-2">
          <div className="w-12 h-12 mx-auto mb-3 bg-emerald-600 rounded-lg flex items-center justify-center">
            <Building2 className="h-6 w-6 text-white" />
          </div>
          <CardTitle className="text-xl">Vendor Portal</CardTitle>
          <CardDescription className="text-sm">APC EstateFlow - Vendor Management</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 w-full mb-4">
              <TabsTrigger value="login" className="text-sm"><LogIn className="h-3 w-3 mr-1" />Login</TabsTrigger>
              <TabsTrigger value="register" className="text-sm"><UserPlus className="h-3 w-3 mr-1" />Register</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-3">
                <div><Label className="text-xs">Email</Label><Input type="email" value={loginForm.email} onChange={e => setLoginForm({...loginForm, email: e.target.value})} required data-testid="vendor-login-email" /></div>
                <div><Label className="text-xs">Password</Label><Input type="password" value={loginForm.password} onChange={e => setLoginForm({...loginForm, password: e.target.value})} required data-testid="vendor-login-password" /></div>
                <Button className="w-full" disabled={loading} data-testid="vendor-login-submit">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <LogIn className="h-4 w-4 mr-2" />}
                  Sign In
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-3">
                <div><Label className="text-xs">Company Name</Label><Input value={registerForm.company_name} onChange={e => setRegisterForm({...registerForm, company_name: e.target.value})} required data-testid="vendor-register-company" /></div>
                <div><Label className="text-xs">Contact Name</Label><Input value={registerForm.contact_name} onChange={e => setRegisterForm({...registerForm, contact_name: e.target.value})} required data-testid="vendor-register-name" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Email</Label><Input type="email" value={registerForm.email} onChange={e => setRegisterForm({...registerForm, email: e.target.value})} required data-testid="vendor-register-email" /></div>
                  <div><Label className="text-xs">Phone</Label><Input value={registerForm.phone} onChange={e => setRegisterForm({...registerForm, phone: e.target.value})} /></div>
                </div>
                <div><Label className="text-xs">Password</Label><Input type="password" value={registerForm.password} onChange={e => setRegisterForm({...registerForm, password: e.target.value})} required data-testid="vendor-register-password" /></div>
                <div><Label className="text-xs">Business Type</Label>
                  <Select value={registerForm.business_type} onValueChange={v => setRegisterForm({...registerForm, business_type: v})}>
                    <SelectTrigger data-testid="vendor-register-type"><SelectValue placeholder="Select type..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="plumbing">Plumbing</SelectItem>
                      <SelectItem value="electrical">Electrical</SelectItem>
                      <SelectItem value="roofing">Roofing</SelectItem>
                      <SelectItem value="hvac">HVAC</SelectItem>
                      <SelectItem value="landscaping">Landscaping</SelectItem>
                      <SelectItem value="painting">Painting</SelectItem>
                      <SelectItem value="general_contractor">General Contractor</SelectItem>
                      <SelectItem value="cleaning">Cleaning</SelectItem>
                      <SelectItem value="security">Security</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">License Number (optional)</Label><Input value={registerForm.license_number} onChange={e => setRegisterForm({...registerForm, license_number: e.target.value})} /></div>
                <Button className="w-full" disabled={loading} data-testid="vendor-register-submit">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
                  Create Account
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default VendorLogin;
